import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Droplets, Waves, Layers, Sprout, TrendingUp, Landmark, ShieldCheck, Quote } from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";
import { Reveal } from "../components/site/Reveal";
import { PageHero, SectionHead, CTABand } from "../components/site/Bits";

const PRODUCT_ROLES = [
  {
    slug: "bonedry", name: "BoneDry", icon: Droplets, category: "Absorbents & Spill Control",
    role: "Keeps hydrocarbons out of the soil.",
    desc: "Encapsulates oil and fuel on contact so spills never leach into the ground. What doesn't penetrate the soil can't contaminate the resource — leaving clean, uncompromised earth behind.",
  },
  {
    slug: "pinkwater", name: "Pinkwater", icon: Waves, category: "Water Treatment & Management",
    role: "Protects the water the land depends on.",
    desc: "Treats problem water on site so contaminants never reach the watershed or groundwater. You safeguard the aquifer and soil chemistry that future crops and herds are built on.",
  },
  {
    slug: "ground-protection-mats", name: "Ground Protection Mats", icon: Layers, category: "Ground Protection & Site Access",
    to: "/ground-protection",
    role: "Preserves soil structure and topsoil.",
    desc: "Our full range of matting — timber, composite, steel, and rubber — distributes heavy loads to prevent rutting, compaction, and topsoil destruction. The right mat for every job means the ground recovers faster and stays productive, instead of being scarred for a generation.",
  },
];

import { Seo } from "../components/site/Seo";

export default function LandStewardship() {
  return (
    <div data-testid="land-stewardship-page">
      <Seo title="Land Stewardship & Resource Preservation" description="OneHarvest helps operators protect land, water, and habitat with responsible ground protection, water treatment, and absorbent solutions." />
      <PageHero
        testid="stewardship-hero"
        image={IMAGES.farmland}
        kicker="Land Stewardship · Resource Preservation"
        title="The project ends. The land keeps producing."
        subtitle="Every drilling program and construction project sits on ground that will outlive it by generations. Protecting that ground today is how landowners keep their asset — and how operators lead."
      />

      {/* THE WINDOW */}
      <section className="bg-background" data-testid="stewardship-window">
        <div className="container-x py-24 grid gap-12 lg:grid-cols-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="kicker text-flame mb-4">A near-term window on a long-cycle asset</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-ink tracking-tight leading-[1.15]">
              Most decisions are made for today's job. The land answers on a longer clock.
            </h2>
            <div className="mt-6 space-y-4 text-muted-foreground text-lg leading-relaxed max-w-2xl">
              <p>
                Operators work in a near-term window — get oil and gas produced, get the project built, get it done efficiently. That focus is right for the job at hand. But the land underneath is a resource that produces on cycles measured in decades, not quarters.
              </p>
              <p>
                That the land is valuable is no secret. What gets overlooked is the <span className="text-ink font-semibold">cyclical pattern</span> of how it produces — and that the way a site is treated today decides what that ground is worth in the next cycle.
              </p>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="grid gap-4">
              {[
                { icon: TrendingUp, t: "Demand is outpacing supply", d: "Capital is quietly buying up land for agriculture and livestock as long-term demand outruns available acreage." },
                { icon: Sprout, t: "Today's site is tomorrow's asset", d: "The ground under today's drilling and construction becomes rich, working land for its owners in future cycles." },
                { icon: Landmark, t: "Condition sets the value", d: "Healthy, uncontaminated land versus deteriorated, potentially contaminated land is the difference in what the asset is worth." },
              ].map((c) => (
                <div key={c.t} className="group flex items-start gap-4 border border-border bg-card p-5 hover:border-flame/50 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]">
                  <div className="flex-shrink-0 h-11 w-11 flex items-center justify-center bg-flame/10 border border-flame/25 text-flame group-hover:bg-flame group-hover:text-white transition-colors duration-300">
                    <c.icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-ink text-base">{c.t}</div>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PULL QUOTE / BANNER */}
      <section className="relative overflow-hidden" data-testid="stewardship-quote">
        <img src={IMAGES.pasture} alt="Healthy pasture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/80" />
        <div className="absolute inset-0 grain opacity-10" />
        <div className="relative container-x py-24">
          <Reveal>
            <Quote className="h-10 w-10 text-flame" />
            <p className="mt-6 font-display font-bold text-white text-2xl md:text-4xl leading-[1.2] tracking-tight max-w-4xl text-balance">
              Risk management and resource preservation aren't a cost of the job. They're how you protect the land's ability to produce — long after the rig and the crew are gone.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PRODUCTS AS PRESERVATION */}
      <section className="bg-charcoal" data-testid="stewardship-products">
        <div className="container-x py-24">
          <SectionHead
            dark
            kicker="One Portfolio, One Mission"
            title="Every product we promote protects the resource"
            desc="BoneDry, Pinkwater, and our ground-protection mats each solve an immediate operational problem — and each does it by keeping the land intact for what comes next."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PRODUCT_ROLES.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <div className="group h-full bg-ink border border-white/10 p-8 flex flex-col hover:border-flame/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 flex items-center justify-center bg-flame/10 border border-flame/25 text-flame group-hover:bg-flame group-hover:text-white transition-colors">
                      <p.icon className="h-6 w-6" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="kicker text-flame">{p.category}</div>
                      <h3 className="font-display font-bold text-xl text-white">{p.name}</h3>
                    </div>
                  </div>
                  <div className="mt-5 font-display font-semibold text-white text-lg leading-snug">{p.role}</div>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">{p.desc}</p>
                  <Link to={p.to || `/products/${p.slug}`} className="mt-6 inline-flex items-center gap-2 text-flame text-sm font-semibold">
                    Explore {p.name} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL VALUE */}
      <section className="bg-background" data-testid="stewardship-value">
        <div className="container-x py-24">
          <SectionHead
            kicker="Good For Both Sides Of The Lease"
            title="A value proposition landowners and operators share"
            desc="Preserving the land isn't a trade-off between production and legacy. It's a win for whoever owns the ground now — and whoever farms or grazes it next."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full group relative border border-border bg-card p-8 overflow-hidden hover:bg-ink hover:border-ink hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,background-color,border-color] duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 flex items-center justify-center bg-flame/10 border border-flame/25 text-flame group-hover:bg-flame group-hover:text-white group-hover:border-flame transition-colors duration-500"><Landmark className="h-5 w-5" strokeWidth={2} /></div>
                  <h3 className="font-display font-bold text-2xl text-ink group-hover:text-white transition-colors duration-500">For Landowners</h3>
                </div>
                <p className="mt-5 text-muted-foreground leading-relaxed group-hover:text-white/70 transition-colors duration-500">
                  Your land is a long-cycle asset. When today's operation ends, healthy, uncontaminated ground lets you fully leverage it for the next use case — premium agriculture and livestock in a market where demand is outrunning supply.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    "Protect soil chemistry, topsoil, and the water table",
                    "Preserve resale and lease value for ag/livestock cycles",
                    "Avoid the cost and stigma of remediation and contamination",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-ink group-hover:text-white transition-colors duration-500">
                      <ShieldCheck className="h-5 w-5 text-flame flex-shrink-0 mt-0.5" />
                      <span className="text-sm leading-snug">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full group relative border border-border bg-card p-8 overflow-hidden hover:bg-ink hover:border-ink hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,background-color,border-color] duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 flex items-center justify-center bg-flame/10 border border-flame/25 text-flame group-hover:bg-flame group-hover:text-white group-hover:border-flame transition-colors duration-500"><TrendingUp className="h-5 w-5" strokeWidth={2} /></div>
                  <h3 className="font-display font-bold text-2xl text-ink group-hover:text-white transition-colors duration-500">For Operators</h3>
                </div>
                <p className="mt-5 text-muted-foreground leading-relaxed group-hover:text-white/70 transition-colors duration-500">
                  Stewarding the land is visionary leadership your organization can own. Turn environmental management from a cost center into a value-add that strengthens landowner relationships, de-risks liability, and demonstrates the kind of foresight stakeholders reward.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    "Add value and enhanced, visionary leadership to your brand",
                    "Strengthen landowner trust and future access",
                    "Reduce environmental liability and support ESG mandates",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-ink group-hover:text-white transition-colors duration-500">
                      <ShieldCheck className="h-5 w-5 text-flame flex-shrink-0 mt-0.5" />
                      <span className="text-sm leading-snug">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
              <Link to="/resource-preservation-flyer" data-testid="stewardship-flyer-cta" className="inline-flex items-center justify-center gap-2 bg-flame text-white px-7 py-4 font-semibold hover:bg-flame/90 transition-colors">
                Resource Preservation flyer <ArrowUpRight className="h-5 w-5" />
              </Link>
              <Link to="/land-stewardship-deck" data-testid="stewardship-deck-cta" className="inline-flex items-center justify-center gap-2 border border-border text-ink px-7 py-4 font-semibold hover:border-flame hover:text-flame transition-colors">
                Operator stewardship deck <ArrowUpRight className="h-5 w-5" />
              </Link>
              <Link to="/request-quote" className="inline-flex items-center justify-center gap-2 border border-border text-ink px-7 py-4 font-semibold hover:border-flame hover:text-flame transition-colors">
                Build a preservation plan
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
